import { Injectable } from '@angular/core';
import { AuthentificationService } from '@app/authentification/authentification.service';
import { NotificationsService } from '@app/components/notification/NotificationsService';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import * as _ from 'lodash';
import {
  catchError,
  forkJoin,
  map,
  mergeMap,
  Observable,
  of,
  shareReplay,
  take,
  tap,
  throwError,
} from 'rxjs';

import { CachedDataService } from '../CachedDataService';
import { AccountDao } from '../account/account.dao';
import { Criteria } from '../criteria.interface';
import { Dao } from '../dao';
import { DataLoaderAuthentified } from '../data-loader-authentified';
import { ReferenceDao } from '../reference/reference.dao';
import {
  GridkyProduct,
  GridKySearchCriteria,
  SimulationResult,
  SimulationType,
} from './simulation.interface';

export interface SimulationCriteria extends Criteria {
  accountId?: number;
  type?: SimulationType[];
}

@Injectable({
  providedIn: 'root',
})
export class SimulationDao extends Dao {
  private simulations: DataLoaderAuthentified<any[]> =
    new DataLoaderAuthentified<any[]>(this.getAuthentified(), user => {
      return this.httpApi.get(`/accounts/${user.accountId}/simulations`);
    });

  constructor(
    httpApi: HttpApiService,
    private accountDao: AccountDao,
    private notificationsService: NotificationsService,
    private referenceDao: ReferenceDao,
    authentificationService: AuthentificationService,
    private cacheService: CachedDataService<SimulationResult<any, any>>,
  ) {
    super(httpApi, authentificationService);
  }

  simulate(
    id: any,
    type: SimulationType,
    parameters: any,
  ): Observable<SimulationResult<any, any>> {
    return this.command(user => {
      const url = new URL(window.location.href).hash;
      return this.httpApi.postGeneric<SimulationResult<any, any>>(
        `/accounts/${user.accountId}/simulations`,
        {
          type: type,
          parameters: parameters,
          id: id,
          originData: {
            __action_page: url,
            __app: 'albr',
          },
        },
      );
    }).pipe(
      tap(simulation => {
        const parameters = { ...simulation.parameters };
        simulation.parameters = simulation.completeParameters;
        if (parameters?.productId)
          simulation.parameters.productId = parameters.productId;
        return simulation;
      }),
    );
  }

  simulateWithCache(
    id: any,
    type: SimulationType,
    parameters: any,
  ): Observable<SimulationResult<any, any>> {
    const key = JSON.stringify({ id, type, parameters });
    const cachedData = this.cacheService.get(key);
    if (cachedData) {
      return cachedData?.pipe(
        tap(
          (simulation: any) =>
            (simulation.parameters = simulation.completeParameters),
        ),
      );
    } else {
      return this.command(user =>
        this.httpApi.postGeneric<SimulationResult<any, any>>(
          `/accounts/${user.accountId}/simulations`,
          {
            type: type,
            parameters: parameters,
            id: id,
          },
        ),
      ).pipe(
        tap(
          simulation => (simulation.parameters = simulation.completeParameters),
        ),
        tap(data => {
          this.cacheService.set(key, data);
        }),
      );
    }
  }

  simulate2(
    id: any,
    type: SimulationType,
    parameters: any,
  ): Observable<SimulationResult<any, any>> {
    return this.command(user =>
      this.httpApi.postGeneric<SimulationResult<any, any>>(
        `/accounts/${user.accountId}/simulations`,
        {
          type: type,
          parameters: parameters,
          id: id,
        },
      ),
    ).pipe(
      tap(
        simulation => (simulation.parameters = simulation.completeParameters),
      ),
      shareReplay(1),
    );
  }

  getSimulation(id) {
    return this.getAuthentified().pipe(
      mergeMap(user =>
        this.httpApi.getGeneric<SimulationResult<any, any>>(
          `/accounts/${user.accountId}/simulations/${id}`,
        ),
      ),
      catchError(error => {
        this.notificationsService.error(
          `Erreur lors du chargement de la simulation ${id}`,
        );
        return throwError(error);
      }),
      tap(
        simulation => (simulation.parameters = simulation.completeParameters),
      ),
    );
  }

  getSimulations() {
    return this.simulations.get();
  }

  download(
    simulation: SimulationResult<any, any>,
    ownerIds?: number[],
  ): Observable<any> {
    const ownerQuery = ownerIds?.length
      ? `?ownerIds=${encodeURIComponent(ownerIds.join(','))}`
      : '';
    const downloadSimulationPdf = simulation =>
      simulation?.id == null
        ? throwError(() => new Error('SIMULATION_NOT_SAVED'))
        : this.httpApi.getWithOptions(
            `/accounts/${simulation.accountId}/simulations/${simulation.id}/pdf${ownerQuery}`,
            {
              observe: 'response',
              responseType: 'blob',
            },
          );

    return this.ensureSimulationResultIsSaved(simulation).pipe(
      mergeMap(downloadSimulationPdf),
    );
  }

  private ensureSimulationResultIsSaved(
    simulation: SimulationResult<any, any>,
  ): Observable<SimulationResult<any, any>> {
    if (!simulation.id) {
      return this.httpApi.postGeneric<SimulationResult<any, any>>(
        `/accounts/${simulation.accountId}/simulations?forceSave=true`,
        simulation,
      );
    } else {
      return of(simulation);
    }
  }

  getProductReference(type: 'PER' | 'ASSURANCE_VIE') {
    return this.getAuthentified().pipe(
      mergeMap(user =>
        this.httpApi.get(
          `/accounts/${user.accountId}/simulations/products/${type}`,
        ),
      ),
    );
  }

  getProductPacks(type: 'PER' | 'ASSURANCE_VIE', productId: string) {
    return this.getAuthentified().pipe(
      mergeMap(user =>
        this.httpApi.get(
          `/accounts/${user.accountId}/simulations/products/${type}/${productId}/packs`,
        ),
      ),
    );
  }

  getScpiPacks(cashOrCredit?: 'CASH' | 'CREDIT') {
    return this.command(user =>
      forkJoin([
        this.httpApi.post(
          `/organizations/${user.organizationId}/scpi-pack/search`,
          cashOrCredit ? { cashOrCredit } : {},
        ),
        this.referenceDao.getScpiSimulation().pipe(take(1)),
      ]).pipe(
        map(this.patchMissingDataFromCorrespondingScpi),
        // _.forEach(coll) === _(coll).forEach() : même valeur émise (collection
        // brute, mutée en place), mais la forme callable `_()` casse sous vitest
        // (interop CJS) — vérifié empiriquement, équivalence incluse.
        map(packs =>
          _.forEach(packs.content, (pack, i) => (pack.index = i + 1)),
        ),
      ),
    );
  }

  getPortfolios() {
    return this.commandAsMainUser(user =>
      this.httpApi.getGeneric<SimulationResult<any, any>>(
        `/user/${user.id}/assets/simulator/assuranceVie/portfolio`,
      ),
    ).pipe(
      map((res: any) => res['data']),
      map((data: any) => data['portfolios']),
    );
  }

  searchGrikey(criteria: GridKySearchCriteria = {}): Observable<GridkyProduct> {
    criteria = {
      distanceSearchLatitude: +44.10113978329256,
      distanceSearchLongitude: +0.9003714690786875,
      distanceSearchRadiusKm: +100,
      priceMin: +criteria.priceMax * 0.9 || 90000,
      priceMax: 100000,
      globalScoreMin: 3.5,
      ...criteria,
    };

    return this.httpApi
      .postGeneric<SimulationResult<any, any>>(`/gridky/properties/search`, {
        page: 1,
        size: 10,
        deleted: false,
        fillExplanations: true,
        sorts: [
          { direction: 'DESC', property: 'GLOBAL_SCORE' },
          { direction: 'ASC', property: 'PRICE' },
        ],
        ...criteria,
      })
      .pipe(map(res => res['content'][0]));
  }

  onUserChange() {
    // NOP
  }

  /* utils */
  patchMissingDataFromCorrespondingScpi([packs, scpis]) {
    packs?.content?.forEach(pack =>
      pack.allocation?.forEach(scpi => {
        const correspondingScpi = scpis.find(s => s.id === scpi.id);

        if (correspondingScpi) {
          scpi.name = correspondingScpi.name || scpi.name;
          scpi.partPrice = _(correspondingScpi.priceHistory).maxBy(
            'date',
          )?.value;
        }
      }),
    );
    return packs;
  }
}
